import { ref, onMounted, onUnmounted, watch, type Ref } from 'vue';

export interface UseScrollIndicatorOptions {
    threshold?: number; // pixels to scroll before hiding indicator
    delay?: number; // ms to wait before showing indicator
    enabled?: Ref<boolean>; // reactive condition to enable/disable
}

export function useScrollIndicator(options: UseScrollIndicatorOptions = {}) {
    const {
        threshold = 100,
        delay = 3000,
        enabled = ref(true)
    } = options;

    const showIndicator = ref(false);
    const hasScrolled = ref(false);
    const timer = ref<ReturnType<typeof setTimeout> | null>(null);

    const handleScroll = () => {
        if (!enabled.value) return;
        
        const scrollY = window.scrollY || document.documentElement.scrollTop;
        
        if (scrollY > threshold) {
            hasScrolled.value = true;
            showIndicator.value = false;
            
            if (timer.value) {
                clearTimeout(timer.value);
                timer.value = null;
            }
        }
    };

    const startTimer = () => {
        // reset state
        hasScrolled.value = false;
        showIndicator.value = false;
        
        // clear any existing timer
        if (timer.value) {
            clearTimeout(timer.value);
        }
        
        // set new timer
        timer.value = setTimeout(() => {
            if (!hasScrolled.value && enabled.value) {
                showIndicator.value = true;
            }
        }, delay);
    };

    const reset = () => {
        showIndicator.value = false;
        hasScrolled.value = false;
        if (timer.value) {
            clearTimeout(timer.value);
            timer.value = null;
        }
    };

    // watch for enabled changes
    watch(enabled, (isEnabled) => {
        if (isEnabled) {
            startTimer();
        } else {
            reset();
        }
    });

    // setup and cleanup
    onMounted(() => {
        window.addEventListener('scroll', handleScroll);
        if (enabled.value) {
            startTimer();
        }
    });

    onUnmounted(() => {
        window.removeEventListener('scroll', handleScroll);
        if (timer.value) {
            clearTimeout(timer.value);
        }
    });

    return {
        showIndicator,
        startTimer,
        reset
    };
}